import { Overrides, FamilyData } from './Product';

const COLOR_TEXT_FEATURE_ID = 'HardwareVariantBasics/PimClassification/1.0/HardwareVariantBasics.colourtext';
const COLOR_FEATURE_ID = 'HardwareVariantBasics/PimClassification/1.0/HardwareVariantBasics.colourhexvalue';
const STORAGE_FEATURE_ID = 'HardwareVariantBasics/PimClassification/1.0/HardwareVariantBasics.internalstoragesize';
const DIMENSION_FEATURE_ID = 'HardwareBasics/PimClassification/1.0/HardwareBasics.dimension';

// TODO: Remove fallbacks
const FALLBACK_COLOR_TEXT_FEATURE_ID = 'Hardware/PimClassification/1.0/Hardware.colourtext';
const FALLBACK_COLOR_FEATURE_ID = 'Hardware/PimClassification/1.0/Hardware.colourhexvalue';
const FALLBACK_STORAGE_FEATURE_ID = 'Hardware/PimClassification/1.0/Hardware.internalstoragesize';
const FALLBACK_DIMENSION_FEATURE_ID = 'Hardware/PimClassification/1.0/Hardware.dimension';

// Features that are internal-only and should not be listed
const BLACKLISTED_FEATURE_IDS = [
	COLOR_TEXT_FEATURE_ID,
	FALLBACK_COLOR_TEXT_FEATURE_ID,
	COLOR_FEATURE_ID,
	FALLBACK_COLOR_FEATURE_ID,
	'HardwareBasics/PimClassification/1.0/HardwareBasics.pixel',
	'Hardware/PimClassification/1.0/Hardware.pixel',
];

interface FeatureUnit {
	name? : string;
	symbol? : string;
	unitType? : string;
}

interface FeatureValue {
	value : string;
}

interface FeatureData {
	code? : string;
	name? : string;
	comparable? : boolean;
	featureUnit? : FeatureUnit;
	featureValues? : FeatureValue[];
	range? : any;
}

export interface ClassificationData {
	code : string;
	features : FeatureData[];
}

interface Features {
	[categoryId : string] : Feature;
}

export interface FeatureGroups {
	[groupId : string] : string[];
}

interface FeatureGroupMap {
	[groupId : string] : FeatureGroup;
}

/**
 * Wrapper object for Features/Details
 */
export default class Details {
	private features : Features;
	private featureOrder : string[];
	private product : Overrides;
	private family : FamilyData;

	constructor(
		classifications : ClassificationData[] = [],
		product : Overrides = {},
		family : FamilyData,
		featureGroups : FeatureGroups = {},
	) {
		this.product = product;
		this.family = family;

		let restGroup : FeatureGroup | null = null;
		const groupMapping : FeatureGroupMap = {};

		this.features = {};
		// Put groups first
		this.featureOrder = Object.keys(featureGroups)
			.map(name => {
				const containedFeatureIDs = featureGroups[name];
				const group = new FeatureGroup(name);
				this.features[group.getFeatureId()] = group;
				if(containedFeatureIDs) {
					containedFeatureIDs.forEach(id => {
						groupMapping[id] = group;
					});
				} else {
					restGroup = group;
				}

				return group.getFeatureId();
			});

		classifications.forEach(classificationCategory => {
			const categoryCode = classificationCategory.code;
			(classificationCategory.features || []).forEach(featureData => {
				const feature = new Feature(categoryCode, featureData);
				const id = feature.getFeatureId();
				this.featureOrder.push(id);
				this.features[id] = feature;
				// Put feature in group if applicable
				if(feature.belongsInGroup()) {
					let group = restGroup;
					if(id in groupMapping) {
						group = groupMapping[id];
					}
					if(group) {
						group.addFeature(feature);
					}
				}
			});
		});

	}

	public getFeatures() {
		return this.featureOrder.map(featureId => this.features[featureId]);
	}

	public getColor() {
		return this.features[COLOR_FEATURE_ID] ||
			this.features[FALLBACK_COLOR_FEATURE_ID] ||
			new Feature(COLOR_FEATURE_ID, {});
	}

	public getColorText() {
		return this.features[COLOR_TEXT_FEATURE_ID] ||
			this.features[FALLBACK_COLOR_TEXT_FEATURE_ID] ||
			new Feature(COLOR_TEXT_FEATURE_ID, {});
	}

	public getMemory() {
		return this.features[STORAGE_FEATURE_ID] ||
			this.features[FALLBACK_STORAGE_FEATURE_ID] ||
			new Feature(STORAGE_FEATURE_ID, {});
	}

	public getDimension() : Feature | undefined {
		return this.features[DIMENSION_FEATURE_ID] || this.features[FALLBACK_DIMENSION_FEATURE_ID];
	}

	public getDeliveryFeatures() {
		return this.product.deliveryFeatures;
	}

	public getGeneralTermsAndConditions() {
		// FIXME NEC: Where to get these?
		return this.product.generalTermsAndConditions || this.family.generalTermsAndConditions || [];
	}

	public getGuaranteePeriod() {
		return this.product.guaranteePeriod || this.family.guaranteePeriod;
	}

	public getConditions() {
		// FIXME NEC: Where to get these?
		return this.product.productConditions || this.family.productConditions;
	}

	public getEcoPoints() {
		// FIXME NEC: Where to get these?
		return this.product.ecoPoints || this.family.ecoPoints;
	}

	public getPowerConsumption() {
		// TODO: Remove fallback
		return this.features['HardwareBasics/PimClassification/1.0/HardwareBasics.powerConsumption'] ||
			this.features['Hardware/PimClassification/1.0/Hardware.powerCosumption'];
	}

	public getProductionEnergy() {
		// TODO: Remove fallback
		return this.features['HardwareBasics/PimClassification/1.0/HardwareBasics.productionEnergy'] ||
			this.features['Hardware/PimClassification/1.0/Hardware.productionEnergy'];
	}

	public getRawMaterialSelection() {
		// FIXME NEC: Where to get these?
		return this.product.rawMaterialSelection || this.family.rawMaterialSelection;
	}
}

export class Feature {
	private category : string;
	private feature : FeatureData;
	constructor(category : string, feature : FeatureData) {
		this.category = category;
		this.feature = feature;
	}
	public getId() {
		return this.feature.code;
	}
	public getCategory() {
		return this.category;
	}
	public getFeatureId() {
		return [this.getCategory(), this.getId()].filter(Boolean).join('/');
	}
	public getName() {
		return this.feature.name || '';
	}
	public isComparable() {
		return !!this.feature.comparable;
	}
	public getUnitName() {
		return (this.feature.featureUnit || {}).name;
	}
	public getUnitSymbol() {
		return (this.feature.featureUnit || {}).symbol;
	}
	public belongsInGroup() {
		return ['true', 'false', true, false].indexOf(this.getValue()) > -1;
	}
	public getValueFormatted() {
		let unit = this.getUnitSymbol();
		unit = unit ? ' ' + unit : '';

		return this.getValue() + unit;
	}
	public getUnitType() {
		return (this.feature.featureUnit || {}).unitType;
	}
	public getValue() {
		return this.getValues()[0];
	}
	public getValues() {
		return (this.feature.featureValues || [])
			.map(featureValue => featureValue.value);
	}
	public hasValueRange() {
		return !!this.feature.range;
	}
	public isBlacklisted() {
		return this.belongsInGroup() || BLACKLISTED_FEATURE_IDS.indexOf(this.getFeatureId()) > -1;
	}
}

export class FeatureGroup extends Feature {
	private name : string;
	private features : Feature[];

	constructor(name : string) {
		super('FeatureGroup', {});
		this.name = name;
		this.features = [];
	}

	public getId() {
		return this.getName();
	}

	public getName() {
		return this.name;
	}

	// tslint:disable-next-line:prefer-function-over-method
	public belongsInGroup() {
		return false;
	}

	public getValue() {
		return this.getValues().join(', ');
	}

	public getValues() {
		return this.features
			.filter(feature => feature.getValue() === 'true')
			.map(feature => feature.getName());
	}

	public isBlacklisted() {
		return !this.getValues().length;
	}

	public addFeature(feature : Feature) {
		this.features.push(feature);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Details.ts